import React, { useState, useMemo } from "react";
import { updateMatchResult } from "services/matchService";
import * as Sentry from "@sentry/react";
import ScoreEditModal from "./scoreEditModal";

const roundOrder = {
  round_of_64: 1,
  round_of_32: 2,
  round_of_16: 3,
  quarterfinal: 4,
  semifinal: 5,
  final: 6,
};

const compareRounds = (roundA, roundB) => {
  const orderA = roundOrder[roundA] ?? 99;
  const orderB = roundOrder[roundB] ?? 99;
  return orderA - orderB;
};

const EliminationMatchesTable = ({ matches, setMatches, tournament, reloadMatches }) => {
  const [editingScores, setEditingScores] = useState({});
  const [editingMatchId, setEditingMatchId] = useState(null);
  const [searchTerms, setSearchTerms] = useState({}); // 🔥 Per-division search
  const [resultFilters, setResultFilters] = useState({}); // 🔥 Per-division filter

  const eliminationMatches = useMemo(
    () =>
      matches
        .filter((m) => m.phase === "elimination")
        .sort((a, b) => compareRounds(a.round, b.round)),
    [matches]
  );

  const divisions = useMemo(() => {
    return Array.from(new Set(eliminationMatches.map((m) => m.division)));
  }, [eliminationMatches]);

  const normalizeText = (text) =>
    text
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .toLowerCase();

  const getTeamDisplayName = (match, teamKey) => {
    if (match[teamKey]?.players) {
      return match[teamKey].players.map((p) => p.name).join(" & ");
    }
    return match[`${teamKey}Name`] || "BYE";
  };

  const handleScoreChange = (matchId, teamKey, value) => {
    setEditingScores((prev) => {
      const current = prev[matchId] || {
        team1: matches.find((m) => m.id === matchId)?.result?.team1 ?? 0,
        team2: matches.find((m) => m.id === matchId)?.result?.team2 ?? 0,
      };
      return {
        ...prev,
        [matchId]: {
          ...current,
          [teamKey]: Math.max(0, parseInt(value, 10) || 0),
        },
      };
    });
  };

  const handleScoreSubmit = async (matchId) => {
    const fallbackMatch = matches.find((m) => m.id === matchId);
    const result = {
      team1:
        editingScores[matchId]?.team1 ?? fallbackMatch?.result?.team1 ?? "",
      team2:
        editingScores[matchId]?.team2 ?? fallbackMatch?.result?.team2 ?? "",
    };

    if (!isNaN(result.team1) && !isNaN(result.team2)) {
      try {
        const newResult =
          result.team1 === 0 && result.team2 === 0 ? null : result;
        await updateMatchResult(matchId, newResult);

        setMatches((prev) =>
          prev.map((m) => (m.id === matchId ? { ...m, result: newResult } : m))
        );

        setEditingMatchId(null);
        setEditingScores((prev) => {
          const updated = { ...prev };
          delete updated[matchId];
          return updated;
        });
      reloadMatches();
      } catch (error) {
        Sentry.captureException(error);
        console.error("Erreur lors de la mise à jour du score :", error);
        alert("❌ Impossible d'enregistrer le score.");
      }
    } else {
      alert("⚠️ Veuillez saisir un score valide pour les deux équipes.");
    }
  };

  if (!eliminationMatches.length) {
    return <p>Aucun match éliminatoire.</p>;
  }

  return (
    <div className="w-full flex flex-wrap gap-6 justify-center">
      {divisions
        .slice() // clone to avoid mutating the original
        .sort((a, b) => {
          if (a === "division1") return -1;
          if (b === "division1") return 1;
          return a.localeCompare(b); // fallback alphabetical
        })
        .map((division) => {
          const search = normalizeText(searchTerms[division] || "");
          const resultFilter = resultFilters[division] || "all";

          const divisionMatches = eliminationMatches.filter(
            (m) => m.division === division
          );

          const filteredMatches = divisionMatches.filter((match) => {
            const names = [
              normalizeText(getTeamDisplayName(match, "team1")),
              normalizeText(getTeamDisplayName(match, "team2")),
            ];
            const matchesSearch =
              !search || names.some((name) => name.includes(search));

            const hasResult = !!match.result;
            const matchesResultFilter =
              resultFilter === "all" ||
              (resultFilter === "with" && hasResult) ||
              (resultFilter === "without" && !hasResult);

            return matchesSearch && matchesResultFilter;
          });

          return (
            <div
              key={division}
              className="w-full md:w-[48%] bg-gray-100 rounded-lg p-4 shadow text-gray-700"
            >
              <h3 className="text-xl font-bold mb-4">
                {division === "division1"
                  ? "Division 1"
                  : division === "division2"
                  ? "Division 2"
                  : "Éliminatoire principal"}
              </h3>

              <div className="flex flex-wrap gap-2 mb-2">
                <input
                  type="text"
                  placeholder="Rechercher équipe..."
                  className="border rounded px-2 py-1 flex-1 text-lg"
                  value={searchTerms[division] || ""}
                  onChange={(e) =>
                    setSearchTerms((prev) => ({
                      ...prev,
                      [division]: e.target.value,
                    }))
                  }
                />
                <select
                  className="border rounded px-2 py-1"
                  value={resultFilters[division] || "all"}
                  onChange={(e) =>
                    setResultFilters((prev) => ({
                      ...prev,
                      [division]: e.target.value,
                    }))
                  }
                >
                  <option value="all">Tous</option>
                  <option value="with">Avec score</option>
                  <option value="without">Sans score</option>
                </select>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full table-auto text-xs md:text-sm">
                  <thead className="bg-sky-900 text-white">
                    <tr>
                      <th className="px-2 py-2">#</th>
                      <th className="px-2 py-2">Home</th>
                      <th className="px-2 py-2">Guest</th>
                      <th className="px-2 py-2">Résultat</th>
                      <th className="px-2 py-2">Round</th>
                      <th className="px-2 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMatches.map((match, idx) => {
                      const team1Name = getTeamDisplayName(match, "team1");
                      const team2Name = getTeamDisplayName(match, "team2");

                      const isEditable =
                        team1Name !== "BYE" &&
                        team2Name !== "BYE" &&
                        team1Name !== "?" &&
                        team2Name !== "?";

                      return (
                        <tr key={match.id} className="border-b">
                          <td className="px-2 py-1 text-center">{idx + 1}</td>
                          <td className="px-2 py-1">{team1Name}</td>
                          <td className="px-2 py-1">{team2Name}</td>
                          <td className="px-2 py-1 text-center">
                            {match.result
                              ? `${match.result.team1} - ${match.result.team2}`
                              : "—"}
                          </td>
                          <td className="px-2 py-1 text-center font-bold">
                            {match.round}
                          </td>
                          <td className="px-2 py-1 text-center">
                            <button
                              onClick={() => setEditingMatchId(match.id)}
                              disabled={!isEditable || tournament.compiled}
                              className={`${
                                (isEditable && !tournament.compiled)
                                  ? "bg-yellow-500 hover:bg-yellow-600"
                                  : "bg-gray-400 cursor-not-allowed"
                              } text-white text-sm px-2 py-1 rounded`}
                            >
                              Modifier
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {filteredMatches.length === 0 && (
                <p className="text-center text-gray-500 italic py-4">
                  Aucun match trouvé.
                </p>
              )}
            </div>
          );
        })}

      {/* Modal */}
      {editingMatchId && (
        <ScoreEditModal
          matchId={editingMatchId}
          score={
            editingScores[editingMatchId] || {
              team1:
                matches.find((m) => m.id === editingMatchId)?.result?.team1 ??
                "",
              team2:
                matches.find((m) => m.id === editingMatchId)?.result?.team2 ??
                "",
            }
          }
          onChange={handleScoreChange}
          onCancel={() => setEditingMatchId(null)}
          onSave={handleScoreSubmit}
          team1Name={getTeamDisplayName(
            matches.find((m) => m.id === editingMatchId),
            "team1"
          )}
          team2Name={getTeamDisplayName(
            matches.find((m) => m.id === editingMatchId),
            "team2"
          )}
        />
      )}
    </div>
  );
};

export default EliminationMatchesTable;
